import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

const PendingSessionsWidget = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [pendingSessions, setPendingSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingSession, setConfirmingSession] = useState(null);
  const [hidingSession, setHidingSession] = useState(null);

  useEffect(() => {
    if (currentUser) {
      fetchPendingSessions();
    }
  }, [currentUser]);

  const fetchPendingSessions = async () => {
    if (!currentUser) return;
    
    try {
      const q = query(
        collection(db, 'sessions'),
        where('providerId', '==', currentUser.uid),
        where('status', '==', 'scheduled'),
        where('hiddenFromProvider', '!=', true)
      );
      
      const querySnapshot = await getDocs(q);
      const sessions = [];
      
      querySnapshot.forEach(doc => {
        sessions.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Sort by creation date (newest first)
      sessions.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
      
      setPendingSessions(sessions);
    } catch (error) {
      console.error('Error fetching pending sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSession = async (sessionId) => {
    setConfirmingSession(sessionId);
    
    try {
      const confirmSession = httpsCallable(functions, 'confirmSession');
      const result = await confirmSession({ sessionId });
      
      if (result.data.success) {
        // Remove the confirmed session from the pending list
        setPendingSessions(prev => prev.filter(session => session.id !== sessionId));
        
        // Show success message or toast here if desired
        console.log('Session confirmed successfully');
      }
    } catch (error) {
      console.error('Error confirming session:', error);
      alert('Failed to confirm session. Please try again.');
    } finally {
      setConfirmingSession(null);
    }
  };

  const handleHideSession = async (sessionId) => {
    setHidingSession(sessionId);
    
    try {
      const sessionRef = doc(db, 'sessions', sessionId);
      await updateDoc(sessionRef, {
        hiddenFromProvider: true,
        hiddenAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Remove the hidden session from the pending list
      setPendingSessions(prev => prev.filter(session => session.id !== sessionId));
      
      console.log('Session hidden successfully');
    } catch (error) {
      console.error('Error hiding session:', error);
      alert('Failed to hide session. Please try again.');
    } finally {
      setHidingSession(null);
    }
  };

  const formatDateTime = (date, time) => {
    if (!date || !time) return 'TBD';
    
    try {
      const [year, month, day] = date.split('-');
      const [hours, minutes] = time.split(':');
      const sessionDate = new Date(year, month - 1, day, hours, minutes);
      
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(sessionDate);
    } catch (error) {
      return `${date} at ${time}`;
    }
  };

  const formatCreatedDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const isSessionInPast = (date, time) => {
    if (!date || !time) return false;
    
    try {
      const [year, month, day] = date.split('-');
      const [hours, minutes] = time.split(':');
      const sessionDateTime = new Date(year, month - 1, day, hours, minutes);
      const now = new Date();
      
      return sessionDateTime < now;
    } catch (error) {
      console.error('Error checking if session is in past:', error);
      return false;
    }
  };

  if (!currentUser) return null;

  return (
    <div className="pending-sessions-widget">
      <div className="widget-header">
        <h2>Pending Session Confirmations</h2>
        <p>Sessions waiting for your confirmation</p>
      </div>
      
      <div className="widget-content">
        {loading ? (
          <div className="loading-state">Loading pending sessions...</div>
        ) : pendingSessions.length === 0 ? (
          <div className="no-pending">
            <p>No pending sessions to confirm</p>
            <span className="sub-text">New session requests will appear here</span>
          </div>
        ) : (
          <div className="pending-sessions-list">
            {pendingSessions.slice(0, 5).map(session => (
              <div key={session.id} className="pending-session-card">
                <div className="session-info">
                  <div className="session-header">
                    <h4>{session.sessionType}</h4>
                    <span className="booking-time">
                      Booked {formatCreatedDate(session.createdAt)}
                    </span>
                  </div>
                  
                  <div className="session-details">
                    <p className="practitioner-name">
                      <strong>Practitioner:</strong> {session.practitionerName}
                    </p>
                    <p className="session-datetime">
                      <strong>Scheduled:</strong> {formatDateTime(session.scheduledDate, session.scheduledTime)}
                    </p>
                    <p className="session-duration">
                      <strong>Duration:</strong> {session.duration} minutes
                    </p>
                    <p className="session-amount">
                      <strong>Amount:</strong> ${session.amount}
                    </p>
                    {session.notes && (
                      <p className="session-notes">
                        <strong>Notes:</strong> {session.notes}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="session-actions">
                  {isSessionInPast(session.scheduledDate, session.scheduledTime) ? (
                    <div className="past-session-notice">
                      <div className="past-session-message">
                        <span className="past-session-icon">⏰</span>
                        <div>
                          <strong>Session time has passed</strong>
                          <p>Please reach out to the practitioner to reschedule this session.</p>
                        </div>
                      </div>
                      <div className="past-session-actions">
                        <button
                          onClick={() => navigate(`/session/${session.id}`)}
                          className="btn-secondary btn-small"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => handleHideSession(session.id)}
                          disabled={hidingSession === session.id}
                          className="btn-hide btn-small"
                        >
                          {hidingSession === session.id ? 'Hiding...' : 'Hide Session'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handleConfirmSession(session.id)}
                        disabled={confirmingSession === session.id}
                        className="btn-confirm"
                      >
                        {confirmingSession === session.id ? 'Confirming...' : 'Confirm Session'}
                      </button>
                      <button
                        onClick={() => navigate(`/session/${session.id}`)}
                        className="btn-secondary btn-small"
                      >
                        View Details
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            
            {pendingSessions.length > 5 && (
              <div className="view-more">
                <button 
                  onClick={() => navigate('/provider-sessions')}
                  className="btn-secondary"
                >
                  View All Pending ({pendingSessions.length})
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingSessionsWidget;